import { ChangeDetectorRef } from '@angular/core';
import { AdminService } from '../../services/admin.service';
import { AdminValidationService } from 'src/app/gamification/command-center/services/admin-validation.service';
import { BudgetSetupService } from '../cv-admin-budget-setup/service/budget-setup.service';
import { BudgetSetupState } from '../../models/budget.model';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { debounceTime, Subscription } from 'rxjs';
import { TabItem, TabType } from '../../models/admin.model';
import { TabsComponent } from '@niceltd/sol/tabs';
import { PointStorePermissionService } from 'src/app/gamification/marketplace/admin/services/point-store-permission.service';
import { AliasPageName } from 'src/app/gamification/cv-marketplace-admin/models/gamification-pages.model';
import { NavigationService } from 'src/app/navigation/navigation.service';

@Component({
    selector: 'cv-admin-landing',
    templateUrl: './cv-admin-landing.component.html',
    styleUrls: ['./cv-admin-landing.component.scss']
})
export class CvAdminLandingComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild("adminTabs") adminTabs: TabsComponent;

    tabList: TabItem[] = [];
    isCxone = true;
    selectedTab = TabType.GAMES;
    adminState;
    budgetSetupState: BudgetSetupState;
    noPMLicense: boolean = null;
    isDataLoading = false;
    hasPermission: boolean;

    aliasTab = [AliasPageName.GamesTab, AliasPageName.ChallengesTab, AliasPageName.AwardsTab, AliasPageName.BudgetTab];
    
    protected readonly tabType = TabType;
    private subscriptionAdminValidation: Subscription = new Subscription();
    private subscriptionBudgetSetup: Subscription = new Subscription();
    private subscriptionLoadingSavingProcess: Subscription = new Subscription();

    constructor(
        private adminService: AdminService,
        private cvConfigService: CvConfigurationService,
        private budgetSetupService: BudgetSetupService,
        private adminValidationService: AdminValidationService,
        private cdr: ChangeDetectorRef,
        private pointStorePermissionService: PointStorePermissionService,
        private navSrvc: NavigationService,
    ) {
        this.cvConfigService.isCxOne = true;
    }

    ngOnInit(): void {
        this.subscriptionBudgetSetup = this.budgetSetupService.budgetSetup$.subscribe(value => {
            this.budgetSetupState = value;
        });
        this.subscriptionAdminValidation = this.adminValidationService.state$.subscribe(value => {
            this.adminState = value;
        });

        this.subscriptionLoadingSavingProcess = this.adminValidationService.isLoadingSavingProcess$.subscribe(isLoading => {
            this.cdr.detectChanges();
        });

        this.getAdminTabs();

        this.noPMLicense = !this.navSrvc.hasPartnerPMAccess;
    }

    ngAfterViewInit(): void {
        this.adminTabs.tabGroup._allTabs.changes
            .pipe(debounceTime(100))
            .subscribe({
                next: () => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const tabParam = urlParams.get('tab');   
                    if (tabParam === 'budget') {
                        this.changeTab(TabType.BUDGET);

                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.subscriptionBudgetSetup.unsubscribe();
        this.subscriptionAdminValidation.unsubscribe();
        this.subscriptionLoadingSavingProcess.unsubscribe();

    }

    async getAdminTabs() {
        try {
            const tabs: TabItem[] = await this.adminService.loadAvailableTabs();
            this.tabList = tabs;
        } catch (error) {
            console.error(error);
        }
    }

    get isLoadingSavingProcess(): boolean {
        return this.adminValidationService?.isLoadingSavingProcess ?? false;
    }

    async onTabChanged(event: MatTabChangeEvent) {
        this.isDataLoading = true;
        await this.loadPermissionList(this.aliasTab[event.index]);
        this.hasPermission = await this.validatePermission('READ', event.index);
        if (this.adminState.isAwardSaving) {
            return;
        }
        if (!this.budgetSetupState.editMode && event.tab.ariaLabel !== TabType.BUDGET) {
            setTimeout(() => {
                this.setTabGroupIndex(TabType.BUDGET);
            }, 0);
            this.budgetSetupService.onBudgetSetupUnsavedChanges(() => {
                this.onSuccessCallbackBudgetSetupModal()
            });
            this.budgetSetupService.budgetSetup.next({
                ...this.budgetSetupService.budgetSetup.value,
                lastIndex: event.index
            });
            return;
        }
        if (!this.adminState.challenges.editMode && event.tab.ariaLabel !== TabType.CHALLENGES) {
            setTimeout(() => {
                this.setTabGroupIndex(TabType.CHALLENGES);
            }, 0);
            this.adminValidationService.showUnsavedChangesDialog(
                () => this.onSuccessCallbackChallengesSettingsModal()
            );

            const currentState = this.adminValidationService.state.value;
            this.adminValidationService.state.next({
                ...currentState,
                challenges: {
                    ...currentState.challenges,
                    lastIndex: event.index
                }
            });
            return;
        }
         if (!this.adminState.awards.editMode && event.tab.ariaLabel !== TabType.AWARDS) {
            setTimeout(() => {
                this.setTabGroupIndex(TabType.AWARDS);
            }, 0);
            this.adminValidationService.showUnsavedChangesDialog(
                () => this.onSuccessCallbackAwardsSettingModal()
            );

            const currentState = this.adminValidationService.state.value;
            this.adminValidationService.state.next({
                ...currentState,
                awards: {
                    ...currentState.awards,
                    lastIndex: event.index
                }
            });
            return;
        }
        if (!this.adminState.games.editMode && event.tab.ariaLabel !== TabType.GAMES) {
            setTimeout(() => {
                this.setTabGroupIndex(TabType.GAMES);
            }, 0);
            this.adminValidationService.showUnsavedChangesDialog(
                () => this.onSuccessCallbackGamesSettingsModal()
            );

            const currentState = this.adminValidationService.state.value;
            this.adminValidationService.state.next({
                ...currentState,
                games: {
                    ...currentState.games,
                    lastIndex: event.index
                }
            });
            return;
        }

        this.selectedTab = event.tab.ariaLabel as TabType;
        this.isDataLoading = false;
    }


    onSuccessCallbackBudgetSetupModal(): void {
        this.budgetSetupService.budgetSetup.next({
            ...this.budgetSetupService.budgetSetup.value,
            editMode: true
        });
        this.setTabGroupIndex(this.budgetSetupState.lastIndex);
    }
    onSuccessCallbackChallengesSettingsModal(): void {
        const currentState = this.adminValidationService.state.value;
        this.adminValidationService.state.next({
            ...currentState,
            challenges: {
                ...currentState.challenges,
                editMode: true
            }
        });
        this.setTabGroupIndex(currentState.challenges.lastIndex);
    }

    onSuccessCallbackAwardsSettingModal(): void {
        const currentState = this.adminValidationService.state.value;
        this.adminValidationService.state.next({
            ...currentState,
            awards: {
                ...currentState.awards,
                editMode: true
            }
        });
        this.setTabGroupIndex(currentState.awards.lastIndex);
    }

    onSuccessCallbackGamesSettingsModal(): void {
        const currentState = this.adminValidationService.state.value;
        this.adminValidationService.state.next({
            ...currentState,
            games: {
                ...currentState.games,
                editMode: true
            }
        });
        this.setTabGroupIndex(currentState.games.lastIndex);
    }

    changeTab(value: number | TabType): void {
        const index = typeof value === 'string' ? this.getTabIndexByType(value) : value;

        if (index === -1) {
            return;
        }

        const tabGroup = this.adminTabs.tabGroup;
        const allTabs = this.adminTabs.tabGroup._allTabs.toArray();
        const tab = allTabs[index];
        if (tab) {
            this.adminTabs.selectedTabChange.emit({ index, tab });
            tabGroup.selectedIndex = index;
        }
    }

    private getTabIndexByType(tabType: TabType): number {
        return this.tabList.findIndex(tab => tab.name === tabType);
    }

    setTabGroupIndex(value: number | TabType): void {
        const index = typeof value === 'string' ? this.getTabIndexByType(value) : value;

        if (index === -1) {
            return;
        }
        const tabGroup = this.adminTabs.tabGroup;
        tabGroup.selectedIndex = index;
    }

    async loadPermissionList(aliasPage) {
        if (!this.cvConfigService.isCxOne) {
            return;
        }
        await this.pointStorePermissionService.loadPermission(
            this.pointStorePermissionService.pageNameFilter(aliasPage)
        );
    }

    async validatePermission(permission: string, tabIndex: number) {
        return await this.pointStorePermissionService.checkPermission(permission,
            this.pointStorePermissionService.pageNameFilter(this.aliasTab[tabIndex]),
        );
    }
}
